import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Loader2, Play, Plus, Trash2, Upload, Users } from 'lucide-react';
import {
  formatIls,
  giftAdmin,
  type GiftCampaign,
  type GiftCardSettings,
  type GiftRecipientRow,
} from '@/lib/giftCards';

interface DraftRow {
  key: string;
  name: string;
  email: string;
  phone: string;
  amount: string;
}

interface RowIssue {
  key: string;
  reason: string;
}

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const newRow = (): DraftRow => ({
  key: crypto.randomUUID(),
  name: '',
  email: '',
  phone: '',
  amount: '',
});

/**
 * Parses pasted text or CSV. Accepts comma, semicolon or tab separators and an
 * optional header line. Column order: name, email, phone, amount — the amount is
 * also detected when it is the only numeric cell.
 */
function parseRows(text: string, defaultAmount: string): DraftRow[] {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const rows: DraftRow[] = [];
  for (const [i, line] of lines.entries()) {
    const cells = line.split(/[,;\t]/).map((c) => c.trim());
    if (i === 0 && /שם|name|email|מייל/i.test(line) && !emailRe.test(cells[1] ?? '')) continue;
    const email = cells.find((c) => emailRe.test(c)) ?? '';
    const numeric = cells.find((c) => c !== '' && /^\d+(\.\d+)?$/.test(c) && !/^0\d/.test(c));
    const phone = cells.find((c) => c !== email && c !== numeric && /^[+0-9\-\s()]{7,}$/.test(c)) ?? '';
    const name = cells.find((c) => c && c !== email && c !== numeric && c !== phone) ?? '';
    rows.push({
      key: crypto.randomUUID(),
      name,
      email,
      phone,
      amount: numeric ?? defaultAmount,
    });
  }
  return rows;
}

export default function GiftBulkEditor({
  settings,
  canIssue,
  emailConfigured,
  onDone,
}: {
  settings: GiftCardSettings;
  canIssue: boolean;
  emailConfigured: boolean;
  onDone: () => void;
}) {
  const [rows, setRows] = useState<DraftRow[]>([newRow()]);
  const [pasteText, setPasteText] = useState('');
  const [defaultAmount, setDefaultAmount] = useState(String(settings.presets_ils?.[0] ?? 250));
  const [messageDefault, setMessageDefault] = useState('');
  const [occasion, setOccasion] = useState('');
  const [senderName, setSenderName] = useState(settings.sender_name ?? '');
  const [deliverAt, setDeliverAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<GiftRecipientRow[] | null>(null);
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [campaigns, setCampaigns] = useState<GiftCampaign[] | null>(null);

  const { validRows, issues, duplicates } = useMemo(() => {
    const seen = new Map<string, number>();
    const issues: RowIssue[] = [];
    const valid: DraftRow[] = [];
    let duplicates = 0;
    for (const r of rows) {
      const name = r.name.trim();
      const email = r.email.trim().toLowerCase();
      const amount = Number(r.amount);
      if (!name && !email && !r.phone.trim() && !r.amount.trim()) continue;
      if (!name) {
        issues.push({ key: r.key, reason: 'חסר שם' });
        continue;
      }
      if (email && !emailRe.test(email)) {
        issues.push({ key: r.key, reason: 'מייל לא תקין' });
        continue;
      }
      if (!email && !r.phone.trim()) {
        issues.push({ key: r.key, reason: 'נדרש מייל או טלפון' });
        continue;
      }
      if (!Number.isFinite(amount) || amount <= 0) {
        issues.push({ key: r.key, reason: 'סכום לא תקין' });
        continue;
      }
      if (amount < Number(settings.min_amount_ils) || amount > Number(settings.max_amount_ils)) {
        issues.push({
          key: r.key,
          reason: `הסכום חייב להיות בין ${settings.min_amount_ils} ל-${settings.max_amount_ils}`,
        });
        continue;
      }
      const dedupeKey = email || r.phone.trim();
      if (seen.has(dedupeKey)) {
        duplicates += 1;
        continue;
      }
      seen.set(dedupeKey, 1);
      valid.push({ ...r, name, email });
    }
    return { validRows: valid, issues, duplicates };
  }, [rows, settings.min_amount_ils, settings.max_amount_ils]);

  const totalAmount = validRows.reduce((sum, r) => sum + Number(r.amount), 0);
  const issueByKey = useMemo(() => new Map(issues.map((i) => [i.key, i.reason])), [issues]);

  const applyPaste = () => {
    const parsed = parseRows(pasteText, defaultAmount);
    if (!parsed.length) return toast.error('לא זוהו שורות');
    setRows((prev) => [...prev.filter((r) => r.name || r.email || r.phone), ...parsed]);
    setPasteText('');
    toast.success(`נוספו ${parsed.length} שורות`);
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    const text = await file.text();
    const parsed = parseRows(text, defaultAmount);
    if (!parsed.length) return toast.error('הקובץ ריק או לא נקרא');
    setRows((prev) => [...prev.filter((r) => r.name || r.email || r.phone), ...parsed]);
    toast.success(`נטענו ${parsed.length} שורות מהקובץ`);
  };

  const run = async () => {
    if (!validRows.length) return toast.error('אין שורות תקינות לשליחה');
    setBusy(true);
    try {
      const idempotency_key = campaignId ?? `bulk-${Date.now()}-${crypto.randomUUID().slice(0, 8)}`;
      const created = await giftAdmin<{ campaign_id: string }>('campaign.create', {
        idempotency_key,
        amount_default: Number(defaultAmount) || null,
        message_default: messageDefault.trim() || null,
        occasion: occasion || null,
        sender_name: senderName.trim() || null,
        deliver_at: deliverAt ? new Date(deliverAt).toISOString() : null,
        rows: validRows.map((r) => ({
          name: r.name,
          email: r.email || null,
          phone: r.phone.trim() || null,
          amount: Number(r.amount),
          message: messageDefault.trim() || null,
        })),
      });
      setCampaignId(created.campaign_id);
      await giftAdmin('campaign.run', { campaign_id: created.campaign_id, issue_reason: 'promotion' });
      const detail = await giftAdmin<{ recipients: GiftRecipientRow[] }>('campaign.detail', {
        campaign_id: created.campaign_id,
      });
      setResults(detail.recipients);
      const failed = detail.recipients.filter((r) => r.status === 'failed').length;
      if (failed) toast.error(`הושלם עם ${failed} כשלים — ניתן לנסות שוב רק את הכשלים`);
      else toast.success('כל השוברים הונפקו');
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ההרצה נכשלה');
    } finally {
      setBusy(false);
    }
  };

  const retryFailed = async () => {
    if (!campaignId) return;
    setBusy(true);
    try {
      await giftAdmin('campaign.run', { campaign_id: campaignId, issue_reason: 'promotion' });
      const detail = await giftAdmin<{ recipients: GiftRecipientRow[] }>('campaign.detail', {
        campaign_id: campaignId,
      });
      setResults(detail.recipients);
      toast.success('ניסיון חוזר הושלם');
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'הניסיון החוזר נכשל');
    } finally {
      setBusy(false);
    }
  };

  const loadCampaigns = async () => {
    try {
      const res = await giftAdmin<{ campaigns: GiftCampaign[] }>('campaign.list');
      setCampaigns(res.campaigns);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'טעינת הקמפיינים נכשלה');
    }
  };

  return (
    <div className="space-y-4">
      <div className="border rounded-xl bg-white p-4 space-y-4">
        <div className="flex items-center gap-2 font-medium">
          <Users className="h-4 w-4" /> הגדרות הקמפיין
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">סכום ברירת מחדל (₪)</Label>
            <Input value={defaultAmount} onChange={(e) => setDefaultAmount(e.target.value)} inputMode="numeric" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">שם השולח/ת</Label>
            <Input value={senderName} onChange={(e) => setSenderName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">אירוע</Label>
            <select
              className="w-full h-10 rounded-md border bg-white px-3 text-sm"
              value={occasion}
              onChange={(e) => setOccasion(e.target.value)}
            >
              <option value="">ללא</option>
              {(settings.occasions ?? []).map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">שליחה מתוזמנת</Label>
            <Input type="datetime-local" value={deliverAt} onChange={(e) => setDeliverAt(e.target.value)} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">הקדשה משותפת</Label>
          <Textarea value={messageDefault} onChange={(e) => setMessageDefault(e.target.value)} rows={2} maxLength={400} />
        </div>
      </div>

      <div className="border rounded-xl bg-white p-4 space-y-3">
        <div className="font-medium">הדבקה או קובץ CSV</div>
        <p className="text-xs text-muted-foreground">
          עמודות: שם, מייל, טלפון, סכום. מותרים פסיק, נקודה-פסיק או טאב. שורת כותרת מזוהה אוטומטית.
        </p>
        <Textarea
          value={pasteText}
          onChange={(e) => setPasteText(e.target.value)}
          rows={4}
          dir="ltr"
          placeholder={'דנה כהן, dana@example.com, 0501234567, 250'}
        />
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={applyPaste} className="gap-1">
            <Plus className="h-3.5 w-3.5" /> הוספת השורות
          </Button>
          <label className="inline-flex">
            <input
              type="file"
              accept=".csv,text/csv,text/plain"
              className="hidden"
              onChange={(e) => onFile(e.target.files?.[0])}
            />
            <span className="inline-flex items-center gap-1 h-9 px-3 rounded-md border text-sm cursor-pointer hover:bg-neutral-100">
              <Upload className="h-3.5 w-3.5" /> טעינת CSV
            </span>
          </label>
        </div>
      </div>

      <div className="border rounded-xl bg-white overflow-hidden">
        <div className="px-4 py-3 border-b flex items-center justify-between gap-2">
          <div className="font-medium">נמענים</div>
          <Button size="sm" variant="outline" onClick={() => setRows((p) => [...p, newRow()])} className="gap-1">
            <Plus className="h-3.5 w-3.5" /> שורה
          </Button>
        </div>
        <div className="divide-y">
          {rows.map((r) => {
            const issue = issueByKey.get(r.key);
            return (
              <div key={r.key} className="p-3 grid grid-cols-1 md:grid-cols-[1.2fr_1.4fr_1fr_0.7fr_auto] gap-2 items-start">
                <Input
                  value={r.name}
                  placeholder="שם"
                  onChange={(e) => setRows((p) => p.map((x) => (x.key === r.key ? { ...x, name: e.target.value } : x)))}
                />
                <Input
                  value={r.email}
                  placeholder="מייל"
                  dir="ltr"
                  onChange={(e) => setRows((p) => p.map((x) => (x.key === r.key ? { ...x, email: e.target.value } : x)))}
                />
                <Input
                  value={r.phone}
                  placeholder="טלפון"
                  dir="ltr"
                  onChange={(e) => setRows((p) => p.map((x) => (x.key === r.key ? { ...x, phone: e.target.value } : x)))}
                />
                <Input
                  value={r.amount}
                  placeholder="סכום"
                  inputMode="numeric"
                  onChange={(e) => setRows((p) => p.map((x) => (x.key === r.key ? { ...x, amount: e.target.value } : x)))}
                />
                <div className="flex items-center gap-2">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="text-muted-foreground hover:text-rose-700"
                    onClick={() => setRows((p) => (p.length > 1 ? p.filter((x) => x.key !== r.key) : p))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                {issue && <div className="md:col-span-5 text-xs text-rose-700">{issue}</div>}
              </div>
            );
          })}
        </div>
      </div>

      <div className="border rounded-xl bg-white p-4 space-y-3">
        <div className="font-medium">סיכום לפני שליחה</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Summary label="שורות תקינות" value={String(validRows.length)} />
          <Summary label="סכום כולל" value={formatIls(totalAmount)} />
          <Summary label="כפילויות שהוסרו" value={String(duplicates)} />
          <Summary label="שורות עם שגיאה" value={String(issues.length)} />
        </div>
        {!emailConfigured && (
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">
            שליחה לא מוגדרת — השוברים יונפקו וניתן להעתיק קישורים אישיים מהרשימה.
          </div>
        )}
        {!canIssue && (
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">
            הנפקה חסומה עד להשלמת ההרשאות בשופיפיי ומפתח ההצפנה. הרשימה נשמרת כטיוטה.
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button onClick={run} disabled={busy || !canIssue || !validRows.length} className="gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} הנפקה ושליחה
          </Button>
          {results?.some((r) => r.status === 'failed') && (
            <Button variant="outline" onClick={retryFailed} disabled={busy}>
              ניסיון חוזר לכשלים
            </Button>
          )}
          <Button variant="ghost" onClick={loadCampaigns} disabled={busy}>
            קמפיינים קודמים
          </Button>
        </div>
      </div>

      {results && (
        <div className="border rounded-xl bg-white overflow-hidden">
          <div className="px-4 py-3 border-b font-medium">תוצאות</div>
          <div className="divide-y text-sm">
            {results.map((r) => (
              <div key={r.id} className="px-4 py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate">{r.name}</div>
                  <div className="text-xs text-muted-foreground truncate" dir="ltr">
                    {r.email ?? r.phone ?? '—'}
                  </div>
                </div>
                <div className="text-end shrink-0">
                  <div>{formatIls(Number(r.amount))}</div>
                  <div className={`text-xs ${r.status === 'failed' ? 'text-rose-700' : 'text-muted-foreground'}`}>
                    {r.status === 'failed' ? r.error ?? 'נכשל' : r.status === 'sent' ? 'נשלח' : r.status === 'scheduled' ? 'מתוזמן' : 'הונפק'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {campaigns && (
        <div className="border rounded-xl bg-white overflow-hidden">
          <div className="px-4 py-3 border-b font-medium">קמפיינים קודמים</div>
          {campaigns.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">אין קמפיינים</div>
          ) : (
            <div className="divide-y text-sm">
              {campaigns.map((c) => (
                <div key={c.id} className="px-4 py-2.5 flex items-center justify-between gap-3">
                  <div>
                    <div>{c.occasion ?? 'ללא אירוע'} · {c.total_recipients} נמענים</div>
                    <div className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleString('he-IL')}</div>
                  </div>
                  <span className="text-xs text-muted-foreground">{c.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Summary({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-neutral-50 p-2.5">
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-medium">{value}</div>
    </div>
  );
}
